import * as React from 'react';
import { cn } from '@/lib/utils';

/**
 * Sober DS (v2) — Segmented. Controlled, non-Radix segmented control for
 * simple option lists. Visual parity with TabsList/TabsTrigger: muted track
 * with inset shadow, active segment lifts onto bg-sober-surface + shadow-so-card.
 */
export interface SegmentedOption {
  value: string;
  label: React.ReactNode;
}

export interface SegmentedProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> {
  options: SegmentedOption[];
  value: string;
  onValueChange: (value: string) => void;
}

export const Segmented = React.forwardRef<HTMLDivElement, SegmentedProps>(
  ({ className, options, value, onValueChange, ...props }, ref) => (
    <div
      ref={ref}
      role="tablist"
      className={cn(
        'inline-flex h-10 items-center gap-1 rounded-so-md bg-sober-muted p-1 shadow-inner',
        className
      )}
      {...props}
    >
      {options.map((option) => {
        const isActive = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            role="tab"
            aria-selected={isActive}
            data-state={isActive ? 'active' : 'inactive'}
            onClick={() => onValueChange(option.value)}
            className={cn(
              'inline-flex items-center justify-center rounded-so-sm px-3.5 py-1.5 text-so-14 font-medium text-sober-secondary transition-colors hover:text-sober-text data-[state=active]:bg-sober-surface data-[state=active]:text-sober-text data-[state=active]:font-bold data-[state=active]:shadow-so-card focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sober-primary-fg'
            )}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  )
);
Segmented.displayName = 'Segmented';
